/**
 * AdminCreateAccount Page Component
 * Administrative interface for creating new user/administrator accounts,
 * sending invitation emails via the backend service, and recording creation audit logs.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabaseClient';
import { appendAuditLogEntry } from '../../utils/auditLog';
import { resolveAdminRole } from '../../utils/adminRole';
import '../../assets/fontello-plus/fontello-73784b09/css/calander.css';
import '../../assets/fontello-change/fontello-79be5077/css/fontello.css';
import logo from '../../assets/finalclipstonedesginlogo-removebg-preview.png';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const MAX_NAME_LENGTH = 200;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const AdminCreateAccount = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [adminInfo, setAdminInfo] = useState(null);
  const [form, setForm] = useState({ name: '', email: '' });
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    const checkAdmin = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();

        if (!session) {
          navigate('/login');
          return;
        }

        const { data: isAdmin, error: rpcError } = await supabase.rpc('check_is_admin', {
          p_email: session.user.email,
        });

        if (rpcError || !isAdmin) {
          navigate('/account');
          return;
        }

        await resolveAdminRole(session.user.email);
        setAdminInfo({ email: session.user.email });
      } catch (err) {
        console.error('Admin create account access check error:', err);
        navigate('/login');
      } finally {
        setLoading(false);
      }
    };

    checkAdmin();
  }, [navigate]);

  const validateForm = (name, email) => {
    if (!name) {
      return 'Name is required.';
    }
    if (name.length > MAX_NAME_LENGTH) {
      return `Name must be ${MAX_NAME_LENGTH} characters or fewer.`;
    }
    if (!email) {
      return 'Email is required.';
    }
    if (!EMAIL_PATTERN.test(email)) {
      return 'Please enter a valid email address.';
    }
    return '';
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setMessage('');

    const trimmedName = form.name.trim();
    const trimmedEmail = form.email.trim().toLowerCase();

    // Client-side validation is a UX convenience only — the backend
    // re-validates and re-authorises everything independently, so this
    // check is not a security boundary.
    const validationError = validateForm(trimmedName, trimmedEmail);
    if (validationError) {
      setError(validationError);
      return;
    }

    setSubmitting(true);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Your session has expired. Please log in again.');
      }

      const response = await fetch(`${BACKEND_URL}/admin/trustees`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          name: trimmedName,
          email: trimmedEmail,
        }),
      });

      if (!response.ok) {
        const errBody = await response.json().catch(() => ({}));
        throw new Error(errBody.detail || `Request failed (${response.status})`);
      }

      appendAuditLogEntry({
        actorEmail: adminInfo?.email,
        action: 'Created',
        entityType: 'admin account',
        entityName: trimmedEmail,
        details: `Added ${trimmedName} to administrator access list.`,
      });

      setMessage(`Admin account access created for ${trimmedEmail}.`);
      setForm({ name: '', email: '' });
    } catch (err) {
      console.error('Create admin account error:', err);
      setError(err.message || 'Could not create admin account access.');
      appendAuditLogEntry({
        actorEmail: adminInfo?.email,
        action: 'Attempted create',
        entityType: 'admin account',
        entityName: trimmedEmail || 'Unknown email',
        details: err.message || 'Create admin account action failed.',
        outcome: 'error',
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div style={styles.page}>
        <div style={styles.card}><p style={styles.mutedText}>Loading create admin account page...</p></div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.logoRow}>
          <img src={logo} alt="Clipstone Miners Welfare Logo" style={styles.logo} />
        </div>

        <div style={styles.header}>
          <div style={styles.adminBadge}>Admin</div>
          <h2 style={styles.title}>Create Admin Account</h2>
          <p style={styles.subtitle}>Grant dashboard access to another administrator</p>
        </div>

        <div style={styles.infoRow}>
          <span style={styles.infoLabel}>Signed in as</span>
          <span style={styles.infoValue}>{adminInfo?.email}</span>
        </div>

        <div style={styles.sectionCard}>
          {error && <div style={styles.errorBanner}>{error}</div>}
          {message && <div style={styles.successBanner}>{message}</div>}

          <form onSubmit={handleSubmit} style={styles.formCard}>
            <input
              style={styles.input}
              value={form.name}
              onChange={(event) => setForm({ ...form, name: event.target.value })}
              placeholder="Full name"
              maxLength={MAX_NAME_LENGTH}
              autoComplete="off"
            />
            <input
              style={styles.input}
              type="email"
              value={form.email}
              onChange={(event) => setForm({ ...form, email: event.target.value })}
              placeholder="Email"
              autoComplete="off"
            />
            <button
              type="submit"
              style={submitting ? { ...styles.primaryBtn, opacity: 0.6, cursor: 'not-allowed' } : styles.primaryBtn}
              disabled={submitting}
            >
              {submitting ? 'Creating...' : 'Create admin access'}
            </button>
          </form>

          <p style={styles.helpText}>
            This uses the existing admin backend endpoint and adds the new person to your admin access list.
          </p>
        </div>

        <div style={styles.actions}>
          <button style={styles.secondaryBtn} onClick={() => navigate('/admin-account')}>
            <i className="icon-left" style={styles.actionIcon} />
            Back to admin dashboard
          </button>
        </div>
      </div>
    </div>
  );
};

const styles = {
  page: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 'clamp(16px, 4vw, 40px) clamp(12px, 3vw, 20px)',
    background: 'linear-gradient(135deg, #0f1f18 0%, #1e3f32 100%)',
    fontFamily: 'Segoe UI, Tahoma, Geneva, Verdana, sans-serif',
  },
  card: {
    background: '#1a2e25',
    border: '1px solid #2d4f3c',
    borderRadius: '16px',
    padding: 'clamp(18px, 4vw, 40px)',
    width: '100%',
    maxWidth: '700px',
    boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
  },
  logoRow: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '24px',
  },
  logo: {
    height: 'clamp(60px, 12vw, 80px)',
    width: 'auto',
  },
  header: {
    textAlign: 'center',
    marginBottom: '20px',
  },
  adminBadge: {
    display: 'inline-block',
    background: '#4ade80',
    color: '#0f1f18',
    fontSize: '11px',
    fontWeight: '800',
    letterSpacing: '0.1em',
    padding: '4px 12px',
    borderRadius: '20px',
    marginBottom: '10px',
  },
  title: {
    color: '#ffffff',
    fontSize: '22px',
    fontWeight: '700',
    margin: '0 0 4px 0',
  },
  subtitle: {
    color: '#6b9e82',
    fontSize: '14px',
    margin: 0,
  },
  infoRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    background: '#152318',
    border: '1px solid #2d4f3c',
    borderRadius: '8px',
    padding: '12px 16px',
    marginBottom: '20px',
    gap: '8px',
  },
  infoLabel: {
    color: '#6b9e82',
    fontSize: '13px',
    fontWeight: '500',
  },
  infoValue: {
    color: '#ffffff',
    fontSize: '13px',
    fontWeight: '600',
    textAlign: 'right',
    wordBreak: 'break-word',
  },
  sectionCard: {
    background: '#152318',
    border: '1px solid #2d4f3c',
    borderRadius: '12px',
    padding: '18px',
    marginBottom: '20px',
  },
  mutedText: {
    color: '#6b9e82',
    fontSize: '14px',
    margin: 0,
  },
  formCard: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  input: {
    width: '100%',
    border: '1px solid #2d4f3c',
    borderRadius: '8px',
    padding: '10px 12px',
    background: '#1a2e25',
    color: '#ffffff',
    fontSize: '13px',
    boxSizing: 'border-box',
  },
  errorBanner: {
    background: '#3a1a1a',
    color: '#f87171',
    border: '1px solid #7f1d1d',
    borderRadius: '8px',
    padding: '8px 10px',
    fontSize: '12px',
    marginBottom: '8px',
  },
  successBanner: {
    background: '#0f2a1a',
    color: '#4ade80',
    border: '1px solid #14532d',
    borderRadius: '8px',
    padding: '8px 10px',
    fontSize: '12px',
    marginBottom: '8px',
  },
  helpText: {
    marginTop: '12px',
    color: '#6b9e82',
    fontSize: '12px',
    lineHeight: 1.5,
  },
  actions: {
    display: 'flex',
    justifyContent: 'center',
  },
  primaryBtn: {
    padding: '12px 14px',
    background: '#4ade80',
    color: '#0f1f18',
    border: 'none',
    borderRadius: '8px',
    fontSize: '15px',
    fontWeight: '700',
    cursor: 'pointer',
  },
  secondaryBtn: {
    padding: '12px 14px',
    background: 'transparent',
    color: '#6b9e82',
    border: '1px solid #2d4f3c',
    borderRadius: '8px',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  actionIcon: {
    display: 'inline-block',
    fontSize: '1.05em',
    marginRight: '8px',
    verticalAlign: 'middle',
  },
};

export default AdminCreateAccount;